import {currencySymbols, walletCurrencies} from 'utils/mock';

/**
 * Shared vocabulary for every swap surface — the list, the action modals and
 * the manual-swap modal all format money, rates and statuses the same way so
 * the same number never shows up in two different shapes.
 */

export const symbolFor = currency =>
  currencySymbols[currency]?.symbol ?? (currency ? `${currency} ` : '');

export const currencyName = currency =>
  currencySymbols[currency]?.name ??
  walletCurrencies.find(item => item.key === currency)?.name ??
  currency;

/** Currency options for every swap select, labelled "NGN — Naira". */
export const currencyOptions = walletCurrencies.map(item => ({
  value: item.key,
  label: `${item.key} — ${item.name}`,
}));

/**
 * Money always reads symbol-first. Whole numbers stay whole, anything with a
 * fraction keeps two decimals, so a fee of 0.5 never renders as "0".
 */
export const formatMoney = (value, currency, {decimals} = {}) => {
  if (value === null || value === undefined || value === '') return '—';
  const amount = Number(value);
  if (Number.isNaN(amount)) return '—';
  const fractionDigits = decimals ?? (Number.isInteger(amount) ? 0 : 2);
  return `${symbolFor(currency)}${amount.toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: Math.max(fractionDigits, 2),
  })}`;
};

/**
 * A rate with a fraction always shows four decimals, so 2.185 reads 2.1850
 * rather than rounding to 2.19 — the difference matters when checking a swap.
 * Whole rates stay whole (1,455). Below 1 the decimals stretch until four
 * significant digits show, so 0.00065 never collapses to 0.0007.
 */
export const formatRateValue = rate => {
  if (rate === null || rate === undefined || rate === '') return '—';
  const amount = Number(rate);
  if (Number.isNaN(amount)) return '—';
  if (Number.isInteger(amount)) return amount.toLocaleString();
  const magnitude = Math.floor(Math.log10(Math.abs(amount)));
  const maximumFractionDigits = Math.min(
    20,
    Math.max(4, magnitude < 0 ? -magnitude + 3 : 4),
  );
  return amount.toLocaleString(undefined, {
    minimumFractionDigits: 4,
    maximumFractionDigits,
  });
};

/** "1 NGN = $0.00065" — the phrasing used everywhere a rate is shown. */
export const formatRate = (rate, sourceCurrency, targetCurrency) => {
  if (rate === null || rate === undefined || rate === '') return '—';
  return `1 ${sourceCurrency ?? ''} = ${symbolFor(
    targetCurrency,
  )}${formatRateValue(rate)}`.trim();
};

export const SWAP_STATUSES = [
  {value: 'PENDING', label: 'Pending', tone: 'pending'},
  {value: 'SUCCESSFUL', label: 'Successful', tone: 'successful'},
  {value: 'FAILED', label: 'Failed', tone: 'failed'},
];

export const statusMeta = status =>
  SWAP_STATUSES.find(item => item.value === status) ?? {
    value: status,
    label: status ?? 'Unknown',
    tone: 'unknown',
  };

/** antd Tag colours, kept in one place so the list and modals never disagree. */
export const statusTagColor = status =>
  ({
    SUCCESSFUL: 'green',
    PENDING: 'orange',
    FAILED: 'red',
  }[status] ?? 'default');

export const userFullName = user =>
  `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();

export const userInitials = user => {
  const initials = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`;
  return initials.toUpperCase() || '—';
};

/**
 * Backend enums arrive as SCREAMING_SNAKE — "THIRD_PARTY_FLUTTERWAVE",
 * "COTE_D_IVOIRE". Admins should read words, not constants.
 */
export const prettifyEnum = value =>
  typeof value === 'string'
    ? value
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, character => character.toUpperCase())
    : '';

/**
 * Balances always show two decimals. The wallet page used to mix
 * `toLocaleString()` (which printed CFA19,593.422) with a two-digit variant on
 * the very next column, so the same kind of number read two different ways.
 */
export const formatBalance = (value, currency) =>
  `${symbolFor(currency)}${Number(value ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
